// react
import { Suspense, useCallback, useEffect, useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
// material ui
import { Card, CardContent, CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';

// shared components
// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/stock';
import 'highcharts/highcharts-more';
import { DateTime } from 'luxon';

// shared components

// components

const toUTC = (timestamp) => {
  if (!timestamp) return null;
  const dt = DateTime.fromISO(timestamp, { zone: 'utc' });
  return dt.isValid ? dt.toMillis() : null;
};

function createArearangeData(
  predicted = [],
  comparison = [],
  useAdvancedModel,
  averageValue,
) {
  if (useAdvancedModel) {
    if (
      !Array.isArray(predicted) ||
      !Array.isArray(comparison) ||
      !predicted.length ||
      !comparison.length
    ) {
      return [];
    }

    const merged = [];

    // Create a map of comparison data by timestamp for efficient lookup
    const comparisonMap = new Map();
    comparison.forEach((point) => {
      if (Array.isArray(point) && point.length >= 2) {
        comparisonMap.set(point[0], point[1]); // timestamp -> value
      }
    });

    // Iterate through predicted data and find matching timestamps
    predicted.forEach((point) => {
      if (!Array.isArray(point) || point.length < 2) return;

      const [xP, yP] = point;
      const yC = comparisonMap.get(xP); // Look up comparison value by timestamp

      if (yC !== undefined) {
        merged.push([xP, yC, yP]);
      }
    });
    return merged;
  }

  if (!Array.isArray(comparison) || !comparison.length) return [];
  const startX = comparison[0][0];
  const endX = comparison[comparison.length - 1][0];
  return [
    [startX, averageValue],
    [endX, averageValue],
  ];
}

const InitiativeChart = ({ selectedInitiative, mode }) => {
  const { t } = useTranslation('energyxplorer-initiatives-tab');
  const theme = useTheme();

  // Theme options for Highcharts (defensive optional chaining)
  const hcTheme = useMemo(() => {
    return theme.palette.mode === 'dark' ? darkTheme : lightTheme;
  }, [theme]);

  // Optional: keep global Highcharts theme in sync (external system sync; no setState)
  useEffect(() => {
    if (hcTheme) Highcharts.setOptions(hcTheme);
  }, [hcTheme]);

  const energyData = useMemo(() => {
    return mode === 'advanced'
      ? selectedInitiative?.savings?.AdvancedEnergySavings
      : selectedInitiative?.savings?.BasicEnergySavings;
  }, [mode, selectedInitiative]);

  const processToSeriesData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    return data.map((entry) => [toUTC(entry.timestamp), entry.value]);
  }, []);

  const seriesOptions = useMemo(() => {
    if (!selectedInitiative) return [];

    const baselineAvg =
      energyData?.EnergySavingsSummary?.AverageBaselineEnergyUsage;
    const comparisonAvg =
      energyData?.EnergySavingsSummary?.AverageComparisonEnergyUsage;

    const baselineStart = toUTC(selectedInitiative?.baselineStart);
    const baselineEnd = toUTC(selectedInitiative?.baselineEnd);
    const reportingStart = toUTC(selectedInitiative?.comparisonStart);
    const reportingEnd = toUTC(selectedInitiative?.comparisonEnd);

    const stepLineData = [
      [baselineStart, baselineAvg],
      [baselineEnd, baselineAvg],
      [reportingStart, comparisonAvg],
      [reportingEnd, comparisonAvg],
    ];

    if (!energyData?.PredictedEnergyUsage) return [];

    return [
      {
        id: 'baseline-energy-usage',
        name: t('chart.baseline_usage'),
        data: processToSeriesData(energyData?.BaselineEnergyUsage),
        type: 'line',
        color: '#42a5f5',
        dataGrouping: { enabled: false },
      },
      {
        id: 'initiative-energy-usage',
        name: t('chart.implementation_usage'),
        data: processToSeriesData(energyData?.InitiativeEnergyUsage),
        type: 'line',
        color: '#0b6477',
        dataGrouping: { enabled: false },
      },
      {
        id: 'comparison-energy-usage',
        name: t('chart.post_implementation_usage'),
        zIndex: 1,
        data: processToSeriesData(energyData?.ComparisonEnergyUsage),
        type: 'line',
        color: '#42a5f5',
        dataGrouping: { enabled: false },
      },
      {
        id: 'predicted-vs-comparison-band',
        type: mode === 'advanced' ? 'arearange' : 'line',
        name:
          mode === 'advanced'
            ? t('chart.projected_usage_without_initiative')
            : t('chart.average'),
        data: createArearangeData(
          processToSeriesData(energyData?.PredictedEnergyUsage),
          processToSeriesData(energyData?.ComparisonEnergyUsage),
          mode === 'advanced',
          baselineAvg,
        ),
        color: mode === 'advanced' ? '#73EC8B' : '#15B392',
        dashStyle: mode === 'advanced' ? undefined : 'Dash',
        fillOpacity: mode === 'advanced' ? 0.3 : 0,
        lineWidth: mode === 'advanced' ? 2 : 4,
        dataGrouping: { enabled: false },
        zIndex: mode === 'advanced' ? 0 : 5,
      },
      {
        id: 'average-usage-baseline',
        name: t('chart.average_usage_baseline'),
        type: 'line',
        step: 'left',
        color: 'red',
        data: stepLineData || [],
        dataGrouping: { enabled: false },
        zIndex: 5,
      },
    ];
  }, [energyData, mode, processToSeriesData, selectedInitiative, t]);

  const plotLineAndBand = useMemo(() => {
    const baselineEndMs = toUTC(selectedInitiative?.baselineEnd);
    const reportingStartMs = toUTC(selectedInitiative?.comparisonStart);

    const plotLines =
      baselineEndMs != null
        ? [
            {
              value: baselineEndMs,
              color: '#0b6477',
              dashStyle: 'Dash',
              width: 2,
              zIndex: 5,
              label: {
                text: selectedInitiative?.name || selectedInitiative?.title,
                style: { color: '#0b6477' },
              },
            },
          ]
        : [];

    const plotBands =
      baselineEndMs != null && reportingStartMs != null
        ? [
            {
              color: '#0b63773a',
              from: baselineEndMs,
              to: reportingStartMs,
              label: { text: '', style: { color: '#0b6477' } },
            },
          ]
        : [];

    return { plotLines, plotBands };
  }, [selectedInitiative]);

  const chartOptions = {
    credits: { enabled: false },
    chart: {
      height: 600,
      zooming: { type: 'x', mouseWheel: { enabled: false } },
      backgroundColor: hcTheme?.chart?.backgroundColor,
    },
    time: { useUTC: true },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      style: { color: hcTheme?.legend?.style?.color },
    },
    rangeSelector: { enabled: false },
    navigator: { enabled: false },
    scrollbar: { enabled: false },
    title: {
      text: '',
      style: { color: hcTheme?.title?.style?.color },
    },
    xAxis: {
      type: 'datetime',
      ordinal: false,
      gridLineWidth: 1,
      plotLines: plotLineAndBand.plotLines,
      plotBands: plotLineAndBand.plotBands,
      gridLineColor: hcTheme?.xAxis?.gridLineColor,
      lineColor: hcTheme?.xAxis?.lineColor,
      labels: { style: { color: hcTheme?.xAxis?.labels?.style?.color } },
      dateTimeLabelFormats: {
        millisecond: '%H:%M:%S.%L',
        second: '%H:%M:%S',
        minute: '%H:%M',
        hour: '%H:%M',
        day: '%a %e. %b',
        week: '%e. %b',
        month: '%b %Y',
        year: '%Y',
      },
    },
    yAxis: {
      title: { text: `${t('chart.energy_usage')} (kWh)` },
      gridLineWidth: 1,
      opposite: false,
      gridLineColor: hcTheme?.yAxis?.gridLineColor,
      labels: { style: { color: hcTheme?.yAxis?.labels?.style?.color } },
    },
    tooltip: {
      shared: true,
      valueSuffix: ' kWh',
      valueDecimals: 2,
      backgroundColor: hcTheme?.tooltip?.backgroundColor,
      borderColor: hcTheme?.tooltip?.borderColor,
    },
    loading: {
      labelStyle: {
        fontWeight: 'bold',
        fontSize: '16px',
        color: '#666',
      },
      style: {
        backgroundColor: 'transparent',
        opacity: 0,
      },
      showDuration: 100,
      hideDuration: 100,
    },
    plotOptions: {
      arearange: { gapSize: 0 },
      line: { gapSize: 0 },
    },
    series: seriesOptions,
  };

  return (
    <>
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '450px',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Card>
          <CardContent sx={{ mt: 2 }}>
            <HighchartsReact
              highcharts={Highcharts}
              constructorType="stockChart"
              options={chartOptions}
            />
          </CardContent>
        </Card>
      </Suspense>
    </>
  );
};

export default InitiativeChart;
